import axios, { AxiosInstance, AxiosResponse } from 'axios'
import { PageResult } from './shared/types'

// * this axios instance will be used for authenticated requests
// * with Authorization header defined
export const API: AxiosInstance = axios.create({})

// Create a separate axios instance for TV endpoints that doesn't include credentials
export const TV_API: AxiosInstance = axios.create({
  withCredentials: false,
})

export const CDN_API: AxiosInstance = axios.create()

// Track the current locale for Accept-Language header
let _currentLocale: string
export const initAPI = ({ apiUrl, apiLocale }: { apiUrl: string; apiLocale: string }) => {
  API.defaults.baseURL = apiUrl
  TV_API.defaults.baseURL = apiUrl

  // Include cookies (session) on all requests
  API.defaults.withCredentials = true

  // Set the initial locale
  _currentLocale = apiLocale

  // Add request interceptor to include 'Accept-Language' header with current locale
  API.interceptors.request.use(config => {
    if (config.headers) {
      config.headers['Accept-Language'] = _currentLocale
    }
    return config
  })

  // Add request interceptor to TV_API for locale
  TV_API.interceptors.request.use(config => {
    if (config.headers) {
      config.headers['Accept-Language'] = _currentLocale
    }
    return config
  })
}

// Keep track of interceptor so we can clean it up later
let _interceptor: number | undefined = undefined
export const unauthenticatedListener = (listener: () => void) => {
  if (_interceptor !== undefined) {
    // Clean up previous interceptors
    API.interceptors.response.eject(_interceptor)
  }

  // Register the new listener
  _interceptor = API.interceptors.response.use(undefined, error => {
    if (error.response && error.response.status === 401) listener()
    // Continue with normal error handling
    return Promise.reject(error)
  })
}

// --- 503 Service Unavailable Listener ---
let _503Interceptor: number | undefined = undefined
let _tv503Interceptor: number | undefined = undefined
/**
 * Registers a listener for 503 Service Unavailable responses on both API instances.
 * Cleans up any previous 503 interceptors before registering new ones.
 * @param listener Function to call when a 503 response is received
 */
export const register503Listener = (listener: () => void) => {
  // Clean up previous interceptors
  if (_503Interceptor !== undefined) {
    API.interceptors.response.eject(_503Interceptor)
  }
  if (_tv503Interceptor !== undefined) {
    TV_API.interceptors.response.eject(_tv503Interceptor)
  }

  // Register 503 listener on main API instance
  _503Interceptor = API.interceptors.response.use(undefined, error => {
    if (error.response && error.response.status === 503) listener()
    return Promise.reject(error)
  })

  // Register 503 listener on TV API instance
  _tv503Interceptor = TV_API.interceptors.response.use(undefined, error => {
    if (error.response && error.response.status === 503) listener()
    return Promise.reject(error)
  })
}

export const unwrapData = <T>(response: AxiosResponse<T>): T => response.data

export const paginateData = <T>(data: T): PageResult<T> => ({
  total: 1,
  results: [data],
  page: 1,
  next: null,
  previous: null,
})

/**
 * Sets the current locale as the Accept-Language header
 * @param locale Language code to use (e.g., 'en', 'es', 'fr')
 */
export const setApiLocale = (locale: string) => {
  _currentLocale = locale
}
